import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {TeamsContextService} from '../../core/teams-context.service';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, filter, Subscription} from 'rxjs';
import {Title} from '@angular/platform-browser';
import {Translation} from '../../../../../micromate-learn-lib/src/lib/services/Translation';
import {LearnerRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/learner-ressource.service';
import {BadgesRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/badges-ressource.service';
import {EventRecorderService} from '../../../../../micromate-learn-lib/src/lib/services/event/event-recorder.service';
import {TabEventType} from '../../../../../micromate-learn-lib/src/lib/services/event/TabEventType';
import {LoggerRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/logger-ressource.service';
import {app} from '@microsoft/teams-js';
import {LearnerBadgesResponseDto} from '../../../../../micromate-learn-lib/src/lib/types/LearnerBadgesResponseDto';
import {BrandingService} from '../../../../../micromate-learn-lib/src/lib/services/branding/branding.service';
import {ProfileLearnerOrganizationService} from '../profile-learner-missing/profile-learner-organization.service';

@Component({
    selector: 'mm-badge',
    templateUrl: './profile-badge.component.html',
    styleUrls: ['./profile-badge.component.scss'],
    standalone: false
})
export class ProfileBadgeComponent implements OnInit, OnDestroy {
    public teamsContextServiceSubscription: Subscription | undefined;
    public badges: BehaviorSubject<LearnerBadgesResponseDto | undefined> = new BehaviorSubject<LearnerBadgesResponseDto | undefined>(undefined);
    public badges$ = this.badges.pipe(filter((value): value is LearnerBadgesResponseDto => value !== undefined));
    public isLearnerMissing = false;

    constructor(public teamsContextService: TeamsContextService,
                public badgesService: BadgesRessource,
                private learnerService: LearnerRessource,
                private translate: TranslateService,
                private ref: ChangeDetectorRef,
                private titleService: Title,
                private eventRecorderService: EventRecorderService,
                private loggerService: LoggerRessource,
                public brandingService: BrandingService,
                private profileLearnerOrganizationService: ProfileLearnerOrganizationService) {
        this.titleService.setTitle('Micromate Profile');
    }

    public ngOnInit(): void {
        this.loggerService.logFrontendInfoToBackend('ProfileBadge init');
        this.teamsContextServiceSubscription = this.teamsContextService.teamsContext$.subscribe(async teamsContext => {
            const userId = teamsContext.user!.id;
            this.loggerService.logFrontendInfoToBackend(`ProfileBadge ${TeamsContextService.formatContext(teamsContext)}, userId: ${userId}`);
            if (userId === undefined) {
                throw new Error(`UserObjectId is undefined when open ProfileBadge, teamsContext: ${TeamsContextService.formatContext(teamsContext)}`);
            }
            // eslint-disable-next-line no-null/no-null
            if (userId === null) {
                throw new Error(`UserObjectId is null when open ProfileBadge, teamsContext: ${TeamsContextService.formatContext(teamsContext)}`);
            }
            if (userId.trim().length === 0) {
                throw new Error(`UserObjectId is empty when open ProfileBadge, teamsContext: ${TeamsContextService.formatContext(teamsContext)}`);
            }
            await this.getLearnerData(userId, teamsContext);
            if (!this.isLearnerMissing) {
                await this.recordTabEvents(userId, teamsContext.page.subPageId);
            }
        });
    }

    public ngOnDestroy(): void {
        this.loggerService.logFrontendInfoToBackend('ProfileBadge destroyed');

        if (this.teamsContextServiceSubscription !== undefined) {
            this.teamsContextServiceSubscription.unsubscribe();
        }
    }

    private async getLearnerData(msteamsId: string, teamsContext: app.Context): Promise<void> {
        this.isLearnerMissing = !(await this.profileLearnerOrganizationService.applyLearnerOrganizationBranding(msteamsId, teamsContext));
        if (this.isLearnerMissing) {
            this.ref.detectChanges();
            return;
        }

        const languageDataPromise = this.learnerService.getLearnerLanguage(msteamsId, teamsContext);
        const badgesPromise = this.badgesService.getLearnerBadges(msteamsId);
        const languageData = await languageDataPromise;
        if (languageData === undefined) {
            throw new Error(`Could not find language in badges for msteamsId: ${msteamsId}`);
        }
        this.translate.use(new Translation().getTranslationLanguage(languageData.language));
        this.badges.next(await badgesPromise);
        this.ref.detectChanges();
    }

    private async recordTabEvents(userId: string, subEntityId: string | undefined | { openInConversation: boolean; }): Promise<void> {
        await this.eventRecorderService.recordTabOpenedEvent(userId, TabEventType.TabBadgeOpened,
            this.eventRecorderService.createTabOpenedEventPayload(subEntityId));
    }

}
